/* First-party analytics SDK — batches events to the main API's POST /events.
 *
 * SECURITY: this module only ever READS DOM attributes (`data-track*`); it never
 * writes HTML and has no injectable sink. Event props are sent as JSON and stored
 * as JSONB; the CRM reads them back through escaped JSX.
 *
 * No consent gate today (members accept T&Cs at signup). `ENABLED` is the single
 * kill-switch and the natural place to hang a consent check if that ever changes.
 * See docs/portal-redesign-crm-plan.md.
 *
 * MERGE NOTE (portal-redesign-crm-merged): dotted convention from the analytics
 * line is canonical — trackError('purchase.payment') => 'purchase.payment_error'.
 * The auto-captures (rage-click / js_error / unhandled_rejection) and
 * trackFormError were ported from the pack-reveal line; they emit their own event
 * names via track(...category:'friction') so they don't pick up the '_error'
 * suffix. A few older call sites still pass a props object as trackError's 2nd
 * arg (the 'reason'); that compiles and still lands as friction — a follow-up
 * pass can normalise those to the dotted signature. */

type Props = Record<string, unknown>;

interface Evt {
  name: string;
  category?: string;
  props?: Props;
  page?: string;
  element?: string;
  referrer?: string;
  t: number;
}

const API = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const ENDPOINT = API + '/events';
const FLUSH_MS = 5000;
const MAX_QUEUE = 40;
const IDLE_MS = 30 * 60 * 1000; // a 30-min gap starts a new session
const RAGE_CLICKS = 3; // N clicks on one element within RAGE_WINDOW_MS => frustration
const RAGE_WINDOW_MS = 1200;
const SCROLL_MILESTONES = [25, 50, 75, 100]; // depth-% thresholds, each emitted once per page
const SCROLL_MIN_PX = 200; // pages with less scrollable height than this produce no signal

const ENABLED = true;

let userId: number | null = null;
let queue: Evt[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let started = false;

let maxScroll = 0; // peak scroll-depth % reached on the current page
let curPageScrollable = false; // did the current page ever produce a real scroll signal
const scrollHit = new Set<number>(); // milestones already emitted for the current page
let scrollRaf = 0; // rAF handle — throttles the scroll handler to one read per frame
let scrollTarget: EventTarget | null = null;

const rid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 10);

function device(): string {
  const w = typeof window !== 'undefined' ? window.innerWidth : 1024;
  return w < 600 ? 'mobile' : w < 1024 ? 'tablet' : 'desktop';
}

function session(): string {
  try {
    const now = Date.now();
    const last = Number(localStorage.getItem('inc_evt_last') || 0);
    let sid = sessionStorage.getItem('inc_evt_sid') || '';
    if (!sid || (last && now - last > IDLE_MS)) {
      sid = rid();
      sessionStorage.setItem('inc_evt_sid', sid);
    }
    localStorage.setItem('inc_evt_last', String(now));
    return sid;
  } catch {
    return 'nostore';
  }
}

let cachedUtm: { source?: string; medium?: string; campaign?: string } | null = null;
function utm() {
  if (cachedUtm) return cachedUtm;
  try {
    const stored = sessionStorage.getItem('inc_evt_utm');
    if (stored) {
      cachedUtm = JSON.parse(stored);
      return cachedUtm!;
    }
    const p = new URLSearchParams(window.location.search);
    cachedUtm = {
      source: p.get('utm_source') || undefined,
      medium: p.get('utm_medium') || undefined,
      campaign: p.get('utm_campaign') || undefined,
    };
    if (cachedUtm.source || cachedUtm.medium || cachedUtm.campaign) {
      sessionStorage.setItem('inc_evt_utm', JSON.stringify(cachedUtm));
    }
    return cachedUtm;
  } catch {
    return {};
  }
}

function scheduleFlush() {
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    flush();
  }, FLUSH_MS);
}

function flush() {
  if (!queue.length || !API) return;
  const batch = queue.splice(0, MAX_QUEUE);
  const body = JSON.stringify({ sessionId: session(), device: device(), utm: utm(), events: batch });
  try {
    // keepalive lets the request survive page unload; /events is CSRF-exempt +
    // credentials:'include' sends the auth cookie so the server can attribute it.
    fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {
      /* best-effort telemetry */
    });
  } catch {
    /* noop */
  }
}

/** Queue a named event. `props` is arbitrary JSON context. */
export function track(name: string, props?: Props, extra?: Partial<Evt>) {
  if (!ENABLED || !name) return;
  queue.push({
    name: String(name).slice(0, 80),
    category: extra?.category,
    props,
    page: extra?.page || (typeof window !== 'undefined' ? window.location.pathname : undefined),
    element: extra?.element,
    referrer: extra?.referrer,
    t: Date.now(),
  });
  if (queue.length >= MAX_QUEUE) flush();
  else scheduleFlush();
}

/* Friction signals — the "why they didn't convert". Both log under category
   'friction' with a consistent `_error` / `_abandon` suffix so the CRM can filter
   pain points and the per-user timeline shows exactly where a flow broke down. */

/** A failed action. `name` 'purchase.payment' → logs 'purchase.payment_error'. */
export function trackError(name: string, reason?: unknown, props?: Props) {
  const r = reason == null ? undefined : String((reason as { message?: string })?.message || reason).slice(0, 300);
  track(`${name}_error`, { reason: r, ...props }, { category: 'friction' });
}

/** A flow the user started but left without completing (cart/checkout abandon). */
export function trackAbandon(name: string, props?: Props) {
  track(`${name}_abandon`, props, { category: 'friction' });
}

/** Report a form validation failure — the fields that blocked submit (or a server
 *  rejection like "email already taken"). `form` names the flow (login/signup/…).
 *  Emits its own 'form_error' event (not suffixed) so both conventions coexist. */
export function trackFormError(form: string, fields: string[], props?: Props) {
  if (!fields || !fields.length) return;
  track(
    'form_error',
    { form, fields, field: fields[0], count: fields.length, ...props },
    { category: 'friction' }
  );
}

/** Attribute the current + future events to a user (called on login). */
export function identify(id: number | null) {
  const changed = userId !== id;
  userId = id;
  if (id && changed) track('identify', undefined, { category: 'session' });
}

let lastPath: string | null = null;
let lastPageAt = 0;
/** Record a page view + the dwell time of the page just left. */
export function page(path: string) {
  const now = Date.now();
  if (lastPath && lastPath !== path) {
    track('page_exit', { dwell_ms: now - lastPageAt, ...scrollExitProps() }, { page: lastPath, category: 'page' });
  }
  lastPath = path;
  lastPageAt = now;
  resetScroll(); // the new page starts its own scroll-depth accounting
  track('page_view', undefined, { page: path, category: 'page' });
}

/* Scroll depth — the second half of page engagement, next to dwell. One passive,
   capture-phase listener catches scrolling from ANY element (the member portal scrolls
   an inner shell container, not the window), rAF-throttled to a single read per frame.
   Each depth milestone fires once per page as its own 'scroll_depth' event, and the peak
   rides along on page_exit so an aggregate "how far do people get" needs no self-join. A
   page with under SCROLL_MIN_PX of scrollable height yields no signal (nothing to scroll),
   so a bounce there is indistinguishable from a short page — fine, dwell already covers it. */
function scrollPctOf(target: EventTarget | null): number {
  try {
    const el =
      !target || target === document || target === window
        ? ((document.scrollingElement as HTMLElement) || document.documentElement)
        : (target as HTMLElement);
    if (!el) return 0;
    const scrollable = el.scrollHeight - el.clientHeight;
    if (scrollable < SCROLL_MIN_PX) return 0;
    return Math.max(0, Math.min(100, Math.round((el.scrollTop / scrollable) * 100)));
  } catch {
    return 0;
  }
}

function onScroll(e: Event) {
  scrollTarget = e.target;
  if (scrollRaf) return;
  scrollRaf = requestAnimationFrame(() => {
    scrollRaf = 0;
    const pct = scrollPctOf(scrollTarget);
    if (pct <= maxScroll) return;
    maxScroll = pct;
    curPageScrollable = true;
    for (const m of SCROLL_MILESTONES) {
      if (pct >= m && !scrollHit.has(m)) {
        scrollHit.add(m);
        track('scroll_depth', { depth: m }, { category: 'page' });
      }
    }
  });
}

function scrollExitProps(): Props {
  return curPageScrollable ? { max_scroll: maxScroll } : {};
}

function resetScroll() {
  maxScroll = 0;
  curPageScrollable = false;
  scrollHit.clear();
}

/* Short, safe label for an element — attribute/text READS only, never written back
   as HTML, so there is no injectable surface. */
function elLabel(el: Element | null): string | undefined {
  if (!el) return undefined;
  const s = (
    el.getAttribute('aria-label') ||
    el.getAttribute('data-track') ||
    el.textContent ||
    ''
  ).trim();
  return s ? s.slice(0, 40) : undefined;
}

/* Global click capture: reads the nearest `data-track` ancestor's attributes only.
   Instrument a button by adding data-track="page.button" (+ optional data-track-*).
   Also detects rage-clicks (>= RAGE_CLICKS on one target inside RAGE_WINDOW_MS). */
let rageEl: Element | null = null;
let rageHits: number[] = [];

function onClick(e: MouseEvent) {
  const start = e.target as Element | null;

  // Rage-click: runs for every click (not just instrumented ones) so we catch
  // controls that feel dead. Emitted as its own friction event (not suffixed).
  const target =
    (start && start.closest && start.closest('button,a,[role="button"],[data-track]')) ||
    start;
  if (target) {
    const now = Date.now();
    if (target === rageEl) {
      rageHits = rageHits.filter((t) => now - t < RAGE_WINDOW_MS);
      rageHits.push(now);
      if (rageHits.length === RAGE_CLICKS) {
        track(
          'rage_click',
          { count: RAGE_CLICKS, label: elLabel(target) },
          { element: (target.tagName || '').toLowerCase(), category: 'friction' }
        );
      }
    } else {
      rageEl = target;
      rageHits = [now];
    }
  }

  const el = start && start.closest ? start.closest('[data-track]') : null;
  if (!el) return;
  const name = el.getAttribute('data-track');
  if (!name) return;
  const props: Props = {};
  for (const attr of Array.from(el.attributes)) {
    if (attr.name.indexOf('data-track-') === 0) props[attr.name.slice(11)] = attr.value;
  }
  track(name, Object.keys(props).length ? props : undefined, {
    element: (el.tagName || '').toLowerCase(),
    category: 'click',
  });
}

/** One-time setup: the global click listener + flush-on-hide + crash capture. */
export function initTracking() {
  if (started || typeof window === 'undefined') return;
  started = true;
  document.addEventListener('click', onClick, true);
  // Capture phase so scrolling inside the portal's inner shell (not just the window) is
  // seen; passive so the listener never blocks the scroll it is measuring.
  window.addEventListener('scroll', onScroll, { capture: true, passive: true });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      if (lastPath) track('page_exit', { dwell_ms: Date.now() - lastPageAt, ...scrollExitProps() }, { page: lastPath, category: 'page' });
      flush();
    }
  });

  // Hard errors: uncaught exceptions + unhandled promise rejections. Deduped so a
  // render loop can't flood the firehose; resource-load errors (no message) and
  // cross-origin "Script error." are dropped as noise. Emitted as their own
  // friction events (js_error / unhandled_rejection), not '_error'-suffixed.
  let lastErr = '';
  let lastErrAt = 0;
  const noteError = (name: string, message: string, props: Props) => {
    const now = Date.now();
    if (message === lastErr && now - lastErrAt < 5000) return;
    lastErr = message;
    lastErrAt = now;
    track(name, props, { category: 'friction' });
  };
  window.addEventListener('error', (ev) => {
    const message = String((ev as ErrorEvent).message || '').slice(0, 200);
    if (!message || message === 'Script error.') return;
    noteError('js_error', message, {
      message,
      source: String((ev as ErrorEvent).filename || '').slice(0, 200),
      line: (ev as ErrorEvent).lineno,
    });
  });
  window.addEventListener('unhandledrejection', (ev) => {
    const reason: any = (ev as PromiseRejectionEvent).reason;
    const message = String((reason && reason.message) || reason || '').slice(0, 200);
    if (!message) return;
    noteError('unhandled_rejection', message, { message });
  });

  window.addEventListener('pagehide', flush);
}
